import React, { useState, useEffect, useRef } from 'react';
import { Share2, X, Copy, Check } from 'lucide-react';
import { toast } from '../hooks/use-toast';

/**
 * ShareProductButton
 * - Mobile: uses Web Share API (native share sheet)
 * - Desktop: opens a popup modal with social share options
 *
 * Always shares the CURRENT page URL (window.location.href)
 * and the given product title.
 */
const ShareProductButton = ({ productTitle = 'Check this out!' }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const modalRef = useRef(null);

  // Always read the live URL at click time so it works for any product page
  const getShareUrl = () => (typeof window !== 'undefined' ? window.location.href : '');
  const getShareText = () => `${productTitle} - Jain Watch Company`;

  const isMobileDevice = () => {
    if (typeof navigator === 'undefined') return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  };

  const handleShareClick = async () => {
    const url = getShareUrl();
    const text = getShareText();

    // Mobile: try native share first
    if (isMobileDevice() && navigator.share) {
      try {
        await navigator.share({ title: productTitle, text, url });
        return;
      } catch (err) {
        // User cancelled or share failed — fall back to modal
        if (err && err.name === 'AbortError') return;
      }
    }

    // Desktop or no native share: open modal
    setOpen(true);
  };

  // Close modal on Escape / outside click
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      toast({ title: 'Link copied!', description: 'Product link copied to clipboard.' });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({ title: 'Copy failed', description: 'Please copy the link manually.', variant: 'destructive' });
    }
  };

  const openShareWindow = (shareUrl) => {
    window.open(shareUrl, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const shareTargets = [
    {
      name: 'WhatsApp',
      testId: 'share-whatsapp',
      bg: 'bg-[#25D366] hover:bg-[#1ebe5d]',
      icon: (
        <svg viewBox="0 0 32 32" className="w-6 h-6 fill-white">
          <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.832 2.722.832.93 0 2.476-.345 2.892-1.32.13-.302.187-.66.187-1.005 0-.07 0-.144-.015-.214-.1-.172-2.32-1.276-2.563-1.276zm-2.1 8.063h-.022c-1.7 0-3.353-.473-4.798-1.336l-.343-.205-3.546.93.946-3.455-.224-.357a9.483 9.483 0 0 1-1.45-5.064c0-5.243 4.267-9.5 9.523-9.5a9.466 9.466 0 0 1 6.737 2.787 9.453 9.453 0 0 1 2.785 6.726c-.013 5.243-4.27 9.5-9.51 9.5zm8.097-17.595A11.317 11.317 0 0 0 17.005 4.3c-6.276 0-11.387 5.1-11.387 11.376 0 2.004.526 3.965 1.524 5.69L5.524 27.7l6.488-1.7a11.378 11.378 0 0 0 5.43 1.386h.007c6.275 0 11.388-5.1 11.388-11.376 0-3.04-1.184-5.9-3.336-8.051z"/>
        </svg>
      ),
      onClick: () =>
        openShareWindow(
          `https://api.whatsapp.com/send?text=${encodeURIComponent(`${getShareText()}\n${getShareUrl()}`)}`
        ),
    },
    {
      name: 'Facebook',
      testId: 'share-facebook',
      bg: 'bg-[#1877F2] hover:bg-[#0e5fc2]',
      icon: (
        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
          <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.99 22 12z"/>
        </svg>
      ),
      onClick: () =>
        openShareWindow(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`
        ),
    },
    {
      name: 'Twitter',
      testId: 'share-twitter',
      bg: 'bg-black hover:bg-gray-800',
      icon: (
        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      onClick: () =>
        openShareWindow(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(getShareText())}&url=${encodeURIComponent(getShareUrl())}`
        ),
    },
    {
      name: 'Telegram',
      testId: 'share-telegram',
      bg: 'bg-[#0088cc] hover:bg-[#006fa5]',
      icon: (
        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
          <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
        </svg>
      ),
      onClick: () =>
        openShareWindow(
          `https://t.me/share/url?url=${encodeURIComponent(getShareUrl())}&text=${encodeURIComponent(getShareText())}`
        ),
    },
    {
      name: 'Gmail',
      testId: 'share-gmail',
      bg: 'bg-[#EA4335] hover:bg-[#c5362b]',
      icon: (
        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
          <path d="M12 13.065L2.4 6h19.2L12 13.065zM2 7.832v10.336c0 .92.747 1.667 1.667 1.667h2.5V12.5L2 7.832zm17.833-1.832L12 11.717 4.167 6h-.5C2.747 6 2 6.747 2 7.667v.165L12 15l10-7.168v-.165C22 6.747 21.253 6 20.333 6h-.5zm2.167 1.832L17.833 12.5v7.335h2.5c.92 0 1.667-.747 1.667-1.667V7.832z"/>
        </svg>
      ),
      onClick: () =>
        openShareWindow(
          `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(getShareText())}&body=${encodeURIComponent(`${getShareText()}\n\n${getShareUrl()}`)}`
        ),
    },
    {
      name: 'Instagram',
      testId: 'share-instagram',
      bg: 'bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 hover:opacity-90',
      icon: (
        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.343 3.608 1.318.974.975 1.255 2.242 1.317 3.608.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.062 1.366-.343 2.633-1.317 3.608-.975.974-2.242 1.255-3.608 1.317-1.266.058-1.646.069-4.85.069-3.204 0-3.584-.012-4.849-.069-1.366-.062-2.633-.343-3.608-1.317-.974-.975-1.255-2.242-1.317-3.608C2.175 15.647 2.163 15.267 2.163 12s.012-3.584.07-4.849c.062-1.366.343-2.633 1.317-3.608.975-.974 2.242-1.255 3.608-1.317C8.416 2.175 8.796 2.163 12 2.163zm0 1.802c-3.155 0-3.523.011-4.766.068-1.024.047-1.582.215-1.95.358-.49.19-.84.418-1.207.785-.367.367-.595.717-.785 1.207-.143.368-.311.926-.358 1.95-.057 1.243-.068 1.611-.068 4.766 0 3.155.011 3.523.068 4.766.047 1.024.215 1.582.358 1.95.19.49.418.84.785 1.207.367.367.717.595 1.207.785.368.143.926.311 1.95.358 1.243.057 1.611.068 4.766.068 3.155 0 3.523-.011 4.766-.068 1.024-.047 1.582-.215 1.95-.358.49-.19.84-.418 1.207-.785.367-.367.595-.717.785-1.207.143-.368.311-.926.358-1.95.057-1.243.068-1.611.068-4.766 0-3.155-.011-3.523-.068-4.766-.047-1.024-.215-1.582-.358-1.95-.19-.49-.418-.84-.785-1.207-.367-.367-.717-.595-1.207-.785-.368-.143-.926-.311-1.95-.358-1.243-.057-1.611-.068-4.766-.068zM12 6.865A5.135 5.135 0 1 0 12 17.135 5.135 5.135 0 0 0 12 6.865zm0 8.468a3.333 3.333 0 1 1 0-6.666 3.333 3.333 0 0 1 0 6.666zm5.338-8.669a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0z"/>
        </svg>
      ),
      onClick: async () => {
        // Instagram has no web URL share — copy link + nudge user
        try {
          await navigator.clipboard.writeText(getShareUrl());
          toast({
            title: 'Link copied for Instagram',
            description: 'Open Instagram and paste the link in your DM or story.',
          });
        } catch {
          toast({ title: 'Please copy the link manually', variant: 'destructive' });
        }
      },
    },
  ];

  return (
    <>
      <button
        onClick={handleShareClick}
        className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
        aria-label="Share product"
        data-testid="product-detail-share-btn"
      >
        <Share2 className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
          data-testid="share-modal-overlay"
          role="dialog"
          aria-modal="true"
        >
          <div
            ref={modalRef}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative animate-in fade-in zoom-in-95 duration-200"
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center"
              aria-label="Close share dialog"
              data-testid="share-modal-close-btn"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            <h3 className="text-lg font-bold text-gray-900 mb-1">Share this product</h3>
            <p className="text-sm text-gray-500 mb-5 line-clamp-2 pr-8">{productTitle}</p>

            <div className="grid grid-cols-4 gap-3 mb-5">
              {shareTargets.map((t) => (
                <button
                  key={t.name}
                  onClick={t.onClick}
                  className="flex flex-col items-center gap-1.5 group"
                  data-testid={t.testId}
                >
                  <span
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-transform group-hover:scale-110 ${t.bg}`}
                  >
                    {t.icon}
                  </span>
                  <span className="text-[11px] text-gray-700 font-medium">{t.name}</span>
                </button>
              ))}
            </div>

            <div className="border-t pt-4">
              <p className="text-xs text-gray-500 mb-2">Or copy link</p>
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-2">
                <input
                  readOnly
                  value={getShareUrl()}
                  className="flex-1 bg-transparent text-xs sm:text-sm text-gray-700 outline-none truncate"
                  data-testid="share-link-input"
                  onFocus={(e) => e.target.select()}
                />
                <button
                  onClick={handleCopy}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    copied
                      ? 'bg-green-500 text-white'
                      : 'bg-yellow-500 hover:bg-yellow-600 text-black'
                  }`}
                  data-testid="share-copy-link-btn"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareProductButton;
